import * as Yup from 'yup';

import Enums from '@core/types/api/Enums';

import { cleanedDecimal, parseDecimalToNumber } from '../utils/decimals';
import { marginPercentage } from '../utils/yupHelpers';

const positiveDecimalValidator = (value: string | null | undefined) =>
  parseDecimalToNumber(cleanedDecimal(value)) >= 0;

export const createMarginPrecisionTest = (
  maxDecimals: number,
): Yup.TestConfig<string | null, Record<string, any>> => ({
  name: `margin-precision-${maxDecimals}`,
  message: `Can have at most ${maxDecimals} decimal place${maxDecimals === 1 ? '' : 's'}`,
  test: (value: string | null): boolean => {
    const cleanedV = cleanedDecimal(value ?? '0');
    // TODO(app.shortcut.com/slabstack/epic/5522): locale-aware separators
    const decimalIndex = cleanedV.indexOf('.');

    if (decimalIndex === -1) return true;

    const decimalPlaces = cleanedV.length - decimalIndex - 1;
    return decimalPlaces <= maxDecimals;
  },
});
//
// As such, marginValidators assumes its returned validators are used within a Yup object
// schema that has both `targetMargin` and `minimumMargin` fields.
// The validators assume that the correct fields always have non-empty values:
// if an entity doesn't have a value, the parent override is displayed.
export const marginValidators = (marginConfiguration: Enums.MarginConfiguration) => {
  const marginPrecision = marginConfiguration === Enums.MarginConfiguration.FlatDollar ? 18 : 3;

  const baseMarginValidator = Yup.string()
    .label('Margin')
    .default('')
    .transform((v) => ((v ?? null) === null ? null : v.replaceAll(',', '')))
    .test('must-be-number', 'Must be a valid number', (v) => !Number.isNaN(parseFloat(v ?? '')))
    .test(createMarginPrecisionTest(marginPrecision));

  const withTest =
    marginConfiguration === Enums.MarginConfiguration.FlatDollar
      ? baseMarginValidator
      : baseMarginValidator.test(marginPercentage);

  const minimumValidator = withTest
    .test('only-positive-values', 'Must be zero or greater than zero', positiveDecimalValidator)
    .test(
      'minimum-lower-or-equal-to-target',
      'Must be less than or equal to target',
      (value, ctx) => {
        const { targetMargin } = ctx.parent as { targetMargin: string };
        const cleanedValue = cleanedDecimal(value);
        if (cleanedValue === '') {
          return true;
        }

        const minimumValue = parseDecimalToNumber(cleanedValue);
        const targetValue = parseDecimalToNumber(cleanedDecimal(targetMargin ?? '0'));
        return minimumValue <= targetValue;
      },
    );

  const targetValidator = withTest
    .test('only-positive-values', 'Must be zero or greater than zero', positiveDecimalValidator)
    .test(
      'target-higher-or-equal-to-minimum',
      'Must be more than or equal to minimum',
      (value, ctx) => {
        const { minimumMargin } = ctx.parent as { minimumMargin: string };
        const cleanedValue = cleanedDecimal(value);
        if (cleanedValue === '') {
          return true;
        }

        const targetValue = parseFloat(cleanedValue ?? '0');
        const minimumValue = parseFloat(minimumMargin ?? '0');
        return targetValue >= minimumValue;
      },
    );

  return { minimumValidator, targetValidator };
};

export const createMarginConfigSchema = (marginConfiguration: Enums.MarginConfiguration) => {
  const { minimumValidator, targetValidator } = marginValidators(marginConfiguration);
  return Yup.object({
    minimumMargin: minimumValidator,
    targetMargin: targetValidator,
  });
};
